import { Link } from 'react-router-dom';
import { BRAND } from '@/config/brand';
import LegalPageLayout from '@/components/layout/LegalPageLayout';

export default function Cookies() {
  return (
    <LegalPageLayout title="Cookie Policy">
      <section>
        <h2>1. What cookies are</h2>
        <p>
          Cookies are small text files stored on your device when you visit a website. Similar technologies include local
          storage, session storage, pixels, and SDKs. This Cookie Policy explains how {BRAND.legalName} (&ldquo;{BRAND.name},&rdquo;
          &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) uses these technologies on the Services. For broader
          privacy practices, see our <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </section>

      <section>
        <h2>2. How {BRAND.name} uses cookies</h2>
        <p>We use cookies and similar technologies to:</p>
        <ul>
          <li>
            <strong>Essential operations</strong> — keep you signed in, protect security, load pages, and remember
            consent or critical settings;
          </li>
          <li>
            <strong>Preferences</strong> — store UI choices such as recently used filters or display preferences;
          </li>
          <li>
            <strong>Analytics / performance</strong> — understand traffic, feature usage, and errors so we can improve
            map search and site reliability;
          </li>
          <li>
            <strong>Marketing (if applicable)</strong> — measure campaigns and, where enabled, support relevant
            advertising about {BRAND.name} or professional products.
          </li>
        </ul>
      </section>

      <section>
        <h2>3. Types of cookies we use</h2>
        <table>
          <thead>
            <tr>
              <th scope="col">Category</th>
              <th scope="col">Purpose</th>
              <th scope="col">Examples</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Strictly necessary</td>
              <td>Required for core site functions and security. The Services may not work properly without these.</td>
              <td>Session/auth cookies, load balancing, CSRF protection</td>
            </tr>
            <tr>
              <td>Performance</td>
              <td>Help us measure how the Services are used and identify performance issues.</td>
              <td>Anonymous analytics identifiers, page-view metrics</td>
            </tr>
            <tr>
              <td>Functionality</td>
              <td>Remember choices that improve your experience across visits.</td>
              <td>Saved filter preferences, locale/UI settings</td>
            </tr>
            <tr>
              <td>Targeting</td>
              <td>Support advertising measurement or interest-based features where used.</td>
              <td>Campaign pixels, ad-partner cookies (when present)</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>4. Map and search-related storage</h2>
        <p>
          To power map search and personalization, we may store data locally or in cookies/local storage, such as recent
          map bounds, search filters, sort options, and (when you are signed in) associations with saved homes or saved
          searches stored on our servers. Clearing site data in your browser will remove locally stored preferences but
          may not delete account-linked saves until you remove them in your dashboard or request deletion under our
          Privacy Policy.
        </p>
      </section>

      <section>
        <h2>5. Managing cookies</h2>
        <p>
          Most browsers let you block or delete cookies through settings. You can usually choose to refuse third-party
          cookies, clear existing cookies, or browse in a private mode. Blocking strictly necessary cookies may prevent
          sign-in, saved sessions, or other core features from working. Check your browser&apos;s help documentation for
          instructions (Chrome, Safari, Firefox, Edge, and others provide cookie controls).
        </p>
        <p>
          You can also control precise location access for map features in your browser or device permission settings,
          independently of cookies.
        </p>
      </section>

      <section>
        <h2>6. Third-party cookies</h2>
        <p>
          Some cookies are set by third parties that provide analytics, maps, payment, advertising, or support tools. Those
          parties may process information according to their own policies. We do not control all third-party cookies; please
          review the relevant provider notices if you want more detail about their practices.
        </p>
      </section>

      <section>
        <h2>7. Updates</h2>
        <p>
          We may update this Cookie Policy when our practices or technologies change. The effective date at the top of this
          page will be revised when updates are posted. For related information about personal data, review our{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </section>

      <section>
        <h2>8. Contact</h2>
        <p>
          Questions about cookies or this policy: {' '}
          <a href={`mailto:${BRAND.support.email}`}>{BRAND.support.email}</a>
          {' · '}
          <Link to="/contact">Contact form</Link>
        </p>
      </section>
    </LegalPageLayout>
  );
}
